import { queryOptions } from '@tanstack/react-query'
import { ApiError, apiExchange, apiGet, apiPostJson } from '../../api/client'
import { saveBytes } from '../../api/inventoree'
import type { MapFinding } from '../map/mapApi'

/**
 * Raportul lunar pe aplicatii - `v4/api/src/Controller/BusinessAppsMonthlyReportController.php`.
 *
 *   GET  /business-apps/monthly-report?company=&month=YYYY-MM|YYYY-Www  raportul (luna sau saptamana)
 *   GET  /business-apps/monthly-report/export.pdf?company=&month=      acelasi pe A4
 *   GET  /business-apps/monthly-report/settings?company=               trimiterea automata
 *   POST /business-apps/monthly-report/settings?company=               {autoSend, recipients, frequency, dayOfWeek, dayOfMonth, hour, timezone}
 *   POST /business-apps/monthly-report/settings/test?company=          {to?, frequency?} - "Send a test now"
 *
 * `company` conteaza numai cu "All companies" in bara; `month` gol = luna trecuta.
 */

export type Availability = { pct: number; downMinutes: number | null } | null

export type ReportTicket = { id: number; subject: string; alarm: boolean; created: string; resolved: string | null }

export type ReportCase = {
  id: number
  ref: string
  ticketId: number
  status: number
  statusLabel: string
  openedAt: string
  dueAt: string | null
  respondedAt: string | null
  closedAt: string | null
  responseHours: number | null
  late: boolean
  overdue: boolean
}

export type ReportApp = {
  id: number
  name: string
  vendor: string
  criticality: number
  criticalityLabel: string
  supportVendor: string
  contractNo: string
  responseHours: number | null
  availability: Availability
  tickets: { total: number; alarms: number; resolved: number; open: number; list: ReportTicket[] }
  cases: {
    total: number
    opened: number
    closed: number
    open: number
    responded: number
    avgResponseHours: number | null
    contractHours: number | null
    breaches: number
    list: ReportCase[]
  }
}

export type ReportSummary = {
  apps: number
  critical: number
  tickets: number
  alarms: number
  openTickets: number
  cases: number
  openCases: number
  breaches: number
  findings: number
  high: number
  availability: number | null
  measured: number
}

export type MonthlyReportPage = {
  ready: boolean
  needsCompany: boolean
  message?: string
  generatedAt: string
  pdfAvailable: boolean
  month: string
  monthLabel: string
  monthToDate: boolean
  /** Perioada e o saptamana ISO (`YYYY-Www`), nu o luna. */
  weekly?: boolean
  /** "1 - 30 Sep 2026", "28 Sep - 4 Oct 2026". */
  periodRange?: string
  months: { key: string; label: string }[]
  weeks?: { key: string; label: string }[]
  canEdit: boolean
  companyId: number
  companyName?: string
  companies?: { id: number; name: string }[]
  summary?: ReportSummary
  apps?: ReportApp[]
  findings?: MapFinding[]
}

export type Frequency = 'weekly' | 'monthly'

/** Programul trimiterii (`ReportSchedule` pe server): `dayOfWeek` 1 = luni; `dayOfMonth` 0 = ultima zi. */
export type ReportScheduleValue = { frequency: Frequency; dayOfWeek: number; dayOfMonth: number; hour: number; timezone: string }

export type ReportSend = {
  period: string
  periodLabel?: string
  status: string
  sentCount: number
  recipients?: number
  sentAt: string | null
  error: string | null
}

export type ReportSettings = {
  ready: boolean
  message?: string
  companyId?: number
  autoSend?: boolean
  recipients?: string[]
  schedule?: ReportScheduleValue
  /** "every Monday at 08:00" - programul SALVAT. */
  scheduleText?: string
  /** Urmatoarea trimitere dupa programul salvat, in fusul lui: "Mon 13 Oct 2026, 08:00". */
  nextLabel?: string
  next?: string
  /** Perioada pe care o duce urmatoarea trimitere: "week of 5 Oct 2026", "October 2026". */
  nextPeriod?: string
  defaultTimezone?: string
  /** `BUSINESS_APPS_REPORT_SEND` aprins pe server; stins = nu pleaca nimic, nici proba. */
  sendingEnabled?: boolean
  /** Adresa omului logat - tinta implicita a probei. */
  me?: string
  maxRecipients?: number
  updatedAt?: string | null
  canEdit?: boolean
  sends?: ReportSend[]
}

export type ReportSettingsDraft = { autoSend: boolean; recipients: string[] } & ReportScheduleValue

export type TestSendResult = { status: 'sent'; to: string; period: string; periodLabel: string; message: string }

function params(values: Record<string, string | number>): string {
  const q = new URLSearchParams()
  for (const [k, v] of Object.entries(values)) {
    if (v !== '' && v !== 0) q.set(k, String(v))
  }
  const s = q.toString()
  return s === '' ? '' : `?${s}`
}

export function monthlyReportQuery(company: number, month: string) {
  return queryOptions({
    queryKey: ['business-apps', 'monthly-report', company, month],
    queryFn: () => apiGet<MonthlyReportPage>(`/business-apps/monthly-report${params({ company, month })}`),
  })
}

export function reportSettingsQuery(company: number) {
  return queryOptions({
    queryKey: ['business-apps', 'monthly-report', 'settings', company],
    queryFn: () => apiGet<ReportSettings>(`/business-apps/monthly-report/settings${params({ company })}`),
  })
}

export function saveReportSettings(company: number, draft: ReportSettingsDraft): Promise<ReportSettings> {
  return apiPostJson<ReportSettings>(`/business-apps/monthly-report/settings${params({ company })}`, draft)
}

/** "Send a test now": o singura scrisoare, la `to` (gol = la mine). Serverul refuza cand trimiterea e stinsa. */
export function sendTestReport(company: number, to: string, frequency: Frequency): Promise<TestSendResult> {
  return apiPostJson<TestSendResult>(`/business-apps/monthly-report/settings/test${params({ company })}`, { to, frequency })
}

/** "Download PDF": cand serverul n-are cu ce sa-l scoata, propozitia lui se arata - nu se salveaza un fisier stricat. */
export async function downloadMonthlyReportPdf(company: number, month: string): Promise<void> {
  const out = await apiExchange(`/business-apps/monthly-report/export.pdf${params({ company, month })}`, { method: 'GET' })
  if (!out.type.includes('pdf')) {
    let message = 'The PDF could not be produced.'
    try {
      message = (JSON.parse(out.text) as { message?: string }).message ?? message
    } catch {
      /* Raspunsul nu era JSON; ramane propozitia de mai sus. */
    }
    throw new ApiError(out.status || 501, message)
  }
  saveBytes(out.bytes, out.type, out.filename, `applications-report_${month}.pdf`)
}
